import { $form, $view, $user, $list } from 'signals/global.signals';
import { createTeam, deleteTeam, updateTeam } from 'api/teams.api';
import { updateUser } from 'api/users.api';
import displayError from 'utils/displayError';
import { handleHideModal, handleShowModal } from 'components/global/_helpers/global.helpers';
import { fetchAndSetInvestorInvites, fetchAndSetInvestorTeamData, fetchAndSetOrganizationUsers } from 'components/views/InvestorTeam/_helpers/investorTeam.resolvers';
import displayNotification from 'utils/displayNotification';
import { deleteInvite, resendInvite, sendInvite } from 'api/invites.api';
import getSelectValue from 'utils/getSelectValue';

export const handleSubmitCreateTeam = async () => {
  try {
    $view.update({
      isModalLoading: true,
    });
    const newTeam = await createTeam({
      name: $form.value.name,
      users: $form.value.users.map((user) => user.value),
    });
    newTeam.team.userCount = newTeam.userOnTeams;
    $list.update({
      data: [...$list.value.data, newTeam.team],
    });
    displayNotification('Team created successfully');
  } catch (error) {
    displayError({
      message: 'Failed to create team. Please check your information and try again.',
      title: 'Team Creation Error',
    });
  } finally {
    $view.update({
      isModalLoading: false,
    });
    handleHideModal();
    $view.update({
      activeTab: 'Teams',
    });
  }
};

export const handleSubmitEditTeam = async () => {
  try {
    $view.update({
      isModalLoading: true,
    });
    await updateTeam($form.value.id, {
      name: $form.value.name,
      users: $form.value.users.map((user) => user.value),
    });
    displayNotification('Team updated successfully');
  } catch (error) {
    displayError({
      message: 'Failed to update team. Please check your information and try again.',
      title: 'Team Update Error',
    });
  } finally {
    $view.update({
      isModalLoading: false,
    });
    handleHideModal();
    await fetchAndSetInvestorTeamData();
  }
};

export const handleSubmitDeleteTeam = async () => {
  try {
    $view.update({
      isModalLoading: true,
    });
    await deleteTeam($form.value.id);
    displayNotification({ message: 'Team deleted successfully', title: 'Team deleted' });
  } catch (error) {
    displayError({
      message: 'Failed to delete team. Please try again.',
      title: 'Team Deletion Error',
    });
  } finally {
    $view.update({
      isModalLoading: false,
    });
    handleHideModal();
    await fetchAndSetInvestorTeamData();
  }
};

export const handleSubmitEditPerson = async () => {
  try {
    $view.update({
      isModalLoading: true,
    });

    const userData = {
      firstName: $form.value.firstName,
      lastName: $form.value.lastName,
      email: $form.value.email,
      roles: $form.value.roles,
      teams: $form.value.teams,
    };

    await updateUser($form.value.id, userData);
    displayNotification({ message: 'User updated successfully', title: 'User updated' });
  } catch (error) {
    displayError({
      message: 'Failed to update user. Please check your information and try again.',
      title: 'User Update Error',
    });
  } finally {
    $view.update({
      isModalLoading: false,
    });
    handleHideModal();
    await fetchAndSetOrganizationUsers();
  }
};

export const handleSubmitDeletePerson = async () => {
  try {
    $view.update({
      isModalLoading: true,
    });
    await updateUser($form.value.id, { archived: true });
    displayNotification({ message: 'User archived successfully', title: 'User archived' });
  } catch (error) {
    displayError({
      message: 'Failed to archive user. Please try again.',
      title: 'User Archive Error',
    });
  } finally {
    $view.update({
      isModalLoading: false,
    });
    handleHideModal();
    await fetchAndSetOrganizationUsers();
  }
};

export const handleClickResendInvite = async (id) => {
  try {
    await resendInvite(id);
    displayNotification({
      message: 'Invite resent successfully',
      title: 'Invite resent',
    });
  } catch (error) {
    displayError({
      message: 'Failed to resend invitation. Please try again.',
      title: 'Resend Invitation Error',
    });
  } finally {
    await fetchAndSetInvestorInvites();
  }
};

export const handleClickDeleteInvite = async (id) => {
  try {
    await deleteInvite(id);
    displayNotification({ message: 'Invite deleted successfully', title: 'Invite deleted' });
  } catch (error) {
    displayError({
      message: 'Failed to delete invitation. Please try again.',
      title: 'Delete Invitation Error',
    });
  } finally {
    await fetchAndSetInvestorInvites();
  }
};

export const handleSubmitSendInvite = async () => {
  try {
    $view.update({
      isModalLoading: true,
    });
    await sendInvite({
      email: $form.value.email,
      teamId: getSelectValue($form.value.team),
      userTypeId: $user.value.userTypeId, // Hardcoded to investor
    });
    displayNotification({ message: 'Invite sent successfully', title: 'Invite sent' });
  } catch (error) {
    displayError({
      message: 'Failed to send invitation. Please check the email address and try again.',
      title: 'Send Invitation Error',
    });
  } finally {
    $view.update({
      isModalLoading: false,
    });
    handleHideModal();
    $view.update({
      activeTab: 'Pending Invites',
    });
    await fetchAndSetInvestorInvites();
  }
};

export const handleClickEditPerson = (person) => {
  handleShowModal('edit-person', {
    id: person.id,
    firstName: person.firstName,
    lastName: person.lastName,
    email: person.email,
    roles: person.roles,
    teams: person.teams,
  });
};

export const handleClickEditTeam = (team) => {
  handleShowModal('edit-team', {
    id: team.id,
    name: team.name,
    users: team.users.map((user) => ({ label: `${user.firstName} ${user.lastName}`, value: user.id })),
  });
};
